import React, { memo } from "react";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import DatePickerField from "./HaklaiDatePickerField";

import * as customColors from "../../../../../theme/customColors";
import { ignoreInvalidCharForNumber, isNumberInRange } from "../../../../../services/utils/utils";
// import "../../index.css";
import HelpDialog from "../../../../layout/HelpDialog";

// Original optional props:
// id: forwarded to the TextField so an external <label htmlFor> can target the input.
// name
// label
// placeholder
// value
// type
// onChange
// onKeyPress
// helperText
// error
// disabled
// multiple
// multiline
// rows

// Extended optional props:
// options: array of {label, value} (relevant for "select" type).
// selectLabelId: id of an external label element that names a "select" field.
// displayEmptySelectOption: show the matching empty-value option label for select fields.
// helpDialog: HTML (enable the help dialog).
// textFieldWidth: TextField width in px deafult 180.
// textFieldStyle: optional styles applied only to the TextField shell.
// labelBoxStyle: optional styles applied only to the label box.
// labelWidth: Label width in px deafult null (auto).
// labelSpacing: Grid spacing between label and input (default 1).
// inputSpacing: Grid spacing between input and currency/icon (default 1).
// noWrap: prevent label/input wrapping to next line.
// background: set background color of textfield to secondary.
// currency: ended with currency symbol.
// bold: set the label as bold.
// labeledTextFieldDirection: set the label and textfield direction.
// minDate: minimum selectable date.
// maxDate: maximum selectable date.

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    maxHeight: 240
  },
  stackLabelOnXsRoot: {
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start"
    }
  },
  stackLabelOnXsLabel: {
    [theme.breakpoints.down("xs")]: {
      marginBottom: "4px !important"
    }
  },
  // The helper sits under the input. Pinning the label to the input height
  // keeps it centered on the field when that helper grows to more than one line.
  labelPinnedToInput: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    height: 34,
    [theme.breakpoints.down("xs")]: {
      height: "auto"
    }
  }
}));

const LabeledTextField = function (props) {
  const classes = useStyles();
  const hasHelperText = props.helperText !== undefined && props.helperText !== null && props.helperText !== "";
  const helperTextTuning =
    props.helperTextMinHeight !== undefined ||
    props.centerHelperText === true ||
    props.helperTextVariableHeight === true ||
    props.helperTextSingleLine === true ||
    props.helperTextNowrap === true;
  const shouldReserveHelperText = helperTextTuning && (hasHelperText || props.helperTextMinHeight !== undefined);
  const helperTextHeight = shouldReserveHelperText ? (props.helperTextMinHeight ?? 22) : 0;

  const saveAsString = function (numStr) {
    return /\.0*$/.test(numStr);
  };

  const handleOnChange = function (e) {
    // Number checking:
    if (props.type === "number" && !props.sequencesOfZerosAllowed) {
      const value = saveAsString(e.target.value) ? e.target.value : parseFloat(ignoreInvalidCharForNumber(e.target.value));
      e.target = { value, name: e.target.name };

      if (Number.isFinite(e.target.value) && isNumberInRange(e.target.value, props.min, props.max)) {
        props.onChange(e);
      } else if (isNumberInRange(parseFloat(e.target.value), props.min, props.max)) {
        props.onChange(e);
      }
    } else {
      props.onChange(e);
    }
  };

  const handleOnBlur = function (e) {
    if (saveAsString(e.target.value)) {
      handleOnChange({ target: { name: e.target.name, value: e.target.value.replace(/\.0*$/, "") } });
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  let val;
  let type = props.type || "";
  if (props.type !== "number") {
    val = props.value || "";
  }
  if (props.type === "number") {
    val = props.value;
    if (!props.sequencesOfZerosAllowed) {
      val = Number.isFinite(props.value) || saveAsString(props.value) ? props.value : "";
    }
    if (!props.noComma) {
      if (Number.isFinite(val)) val = val.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    type = "text";
  }

  // Set the Background color and set its borderRadius only if needed.
  let background = customColors.white;
  if (props.background) background = customColors.orange;
  else if (props.disabled) background = customColors.veryLigntGray;
  const inputBackgroundOnly = props.inputBackgroundOnly === true;
  const textFieldStyle = {
    // Set the TextField Width (default is 190px).
    width: props.textFieldWidth ? props.textFieldWidth + "px" : "190px",
    ...(!inputBackgroundOnly
      ? {
          background,
          borderRadius: props.background || props.disabled ? "5px" : null
        }
      : {}),
    ...(props.textFieldStyle || {})
  };
  const inputShellStyle = {
    background,
    borderRadius: props.background || props.disabled ? "5px" : null
  };
  const labelSpacing = props.labelSpacing ?? 1;
  const inputSpacing = props.inputSpacing ?? 1;
  const wrap = props.noWrap ? "nowrap" : "wrap";
  const helperTextStyle = {
    lineHeight: 1.25,
    whiteSpace: "normal",
    minHeight: helperTextHeight,
    height: "auto",
    overflow: "visible"
  };
  if (props.centerHelperText) {
    helperTextStyle.textAlign = "center";
    helperTextStyle.marginLeft = 0;
    helperTextStyle.marginRight = 0;
  }
  if (props.helperTextSingleLine === true) {
    helperTextStyle.whiteSpace = "nowrap";
    helperTextStyle.overflow = "hidden";
    helperTextStyle.textOverflow = "ellipsis";
    helperTextStyle.maxWidth = props.textFieldWidth != null ? `${props.textFieldWidth}px` : textFieldStyle.width;
  }
  if (props.helperTextNowrap === true) {
    // Keep the message on one line. margin auto centers it under the input
    // in RTL, where a left offset would shift it off the field.
    helperTextStyle.whiteSpace = "nowrap";
    helperTextStyle.overflow = "visible";
    helperTextStyle.textOverflow = "clip";
    helperTextStyle.width = "max-content";
    helperTextStyle.maxWidth = "none";
    helperTextStyle.marginLeft = "auto";
    helperTextStyle.marginRight = "auto";
  }
  const helperTextProps = helperTextTuning
    ? {
        className: props.helperTextClassName,
        style: helperTextStyle,
        ...(props.helperTextSingleLine === true && hasHelperText ? { title: String(props.helperText) } : {})
      }
    : undefined;
  const selectDisplayStyle = props.selectTextColor
    ? { color: props.selectTextColor }
    : undefined;
  const selectMenuProps = {
    classes: { paper: classes.menuPaper },
    ...(props.openMenuBelow
      ? {
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" }
        }
      : {})
  };
  const emptySelectOption = props.type === "select" && props.displayEmptySelectOption
    ? (props.options || []).find((option) => option.value === "")
    : null;
  const selectIsLocked = props.type === "select" && props.lockSelect === true;
  const selectProps = {
    multiple: props.multiple,
    MenuProps: selectMenuProps,
    ...(selectIsLocked
      ? {
          open: false,
          IconComponent: () => null,
          onOpen: (event) => event.preventDefault()
        }
      : {}),
    ...(props.selectLabelId ? { labelId: props.selectLabelId } : {}),
    ...(emptySelectOption
      ? {
          displayEmpty: true,
          renderValue: (selected) => {
            const selectedOption = (props.options || []).find((option) => option.value === selected);
            return selectedOption ? selectedOption.label : "";
          }
        }
      : {}),
    ...(selectDisplayStyle
      ? {
          style: selectDisplayStyle,
          SelectDisplayProps: { style: selectDisplayStyle }
        }
      : {})
  };
  const inputProps = {
    readOnly: selectIsLocked || undefined,
    style: {
      WebkitBoxShadow: "0 0 0 1000px " + background + " inset",
      ...(props.inputTextColor || props.selectTextColor ? { color: props.inputTextColor || props.selectTextColor } : {})
    },
    maxLength: props.maxLength
  };
  const normalizedHelperText = shouldReserveHelperText ? (hasHelperText ? props.helperText : " ") : props.helperText;
  const pinLabelToInput = props.pinLabelToInput === true;
  const labelBottomMargin = pinLabelToInput
    ? 0
    : shouldReserveHelperText
      ? `${helperTextHeight}px`
      : (props.helperText ? "22px" : "");
  const hasBelowInputSlot = props.belowInputText !== undefined && props.belowInputText !== null;
  const belowInputMinHeight = props.belowInputMinHeight ?? 18;
  const inputColumnStyle = hasBelowInputSlot
    ? {
        position: "relative",
        paddingBottom: belowInputMinHeight
      }
    : undefined;
  const alignItems = props.labeledTextFieldDirection === "column" || pinLabelToInput
    ? "flex-start"
    : hasBelowInputSlot
      ? "flex-start"
      : "center";

  return (
    <Grid
      container
      item
      direction={props.labeledTextFieldDirection ? props.labeledTextFieldDirection : "row"}
      spacing={labelSpacing}
      wrap={wrap}
      alignItems={alignItems}
      className={props.stackLabelOnXs ? classes.stackLabelOnXsRoot : undefined}
    >
      {props.label ? (
        <Grid item>
          <Box
            className={[
              props.stackLabelOnXs ? classes.stackLabelOnXsLabel : "",
              pinLabelToInput ? classes.labelPinnedToInput : ""
            ].filter(Boolean).join(" ") || undefined}
            width={props.labelWidth ? props.labelWidth : null}
            style={props.labelBoxStyle}
            /*style={{verticalAlign: "1px"}}*/ marginBottom={labelBottomMargin}
          >
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Typography variant="subtitle1" align="right" style={{ fontWeight: props.bold ? "bold" : null }}>
                  {props.label}
                </Typography>
              </Grid>
              {props.helpDialog ? (
                <Grid item>
                  <HelpDialog helpDialog={props.helpDialog} label={props.label} />
                </Grid>
              ) : null}
            </Grid>
          </Box>
        </Grid>
      ) : null}

      <Grid item>
        <Grid container direction="column" spacing={0} style={inputColumnStyle}>
          <Grid item>
            <Grid container direction="row" spacing={inputSpacing} alignItems="center" wrap={wrap}>
              <Grid item>
                {props.type === "date" ? (
                  <DatePickerField
                    disabled={props.disabled}
                    value={val}
                    name={props.name}
                    error={props.error}
                    helperText={normalizedHelperText}
                    helperTextMinHeight={shouldReserveHelperText ? helperTextHeight : undefined}
                    invalidInputMessage={props.invalidInputMessage}
                    validateInvalidInputOnBlur={props.validateInvalidInputOnBlur}
                    centerHelperText={props.centerHelperText}
                    helperTextSingleLine={props.helperTextSingleLine}
                    textFieldWidth={props.textFieldWidth}
                    exactWidth={props.exactWidth}
                    background={props.background}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    placeholder={props.placeholder}
                    className="width100-sm"
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                  />
                ) : (
                  <TextField
                    id={props.id}
                    disabled={props.disabled}
                    size="small"
                    align="right"
                    style={textFieldStyle}
                    value={val}
                    type={type}
                    select={props.type === "select"}
                    SelectProps={selectProps}
                    variant="outlined"
                    placeholder={props.placeholder}
                    helperText={normalizedHelperText}
                    FormHelperTextProps={helperTextProps}
                    InputProps={inputBackgroundOnly ? { style: inputShellStyle } : undefined}
                    rows={props.rows}
                    multiline={props.multiline}
                    inputProps={inputProps}
                    error={props.error}
                    name={props.name}
                    className="width100-sm"
                    onKeyPress={props.onKeyPress}
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                  >
                    {props.type === "select" && props.options
                      ? props.options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      : null}
                  </TextField>
                )}
              </Grid>
              {props.currency ? (
                <Grid item>
                  {shouldReserveHelperText ? (
                    <Box marginBottom={`${helperTextHeight}px`}>
                      <Typography align="right" variant="subtitle1">
                        {props.currency}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography align="right" variant="subtitle1">
                      {props.currency}
                    </Typography>
                  )}
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          {hasBelowInputSlot ? (
            <Typography
              variant="caption"
              className={props.belowInputClassName}
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                display: "block",
                minHeight: belowInputMinHeight,
                lineHeight: 1.25,
                whiteSpace: "nowrap"
              }}
            >
              {props.belowInputText || " "}
            </Typography>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo(LabeledTextField);
